/**
 * DocumentChangeOverlay - Visual overlay for displaying tracked changes in documents
 * 
 * Features:
 * - Colored highlights for insertions, deletions, formatting
 * - User-specific color coding
 * - Hover tooltips with change details
 * - Integration with VisualChangeTracker
 */

import React, { useMemo } from 'react';
import type { VisualChange } from './VisualChangeTracker';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface DocumentChangeOverlayProps {
  documentContent: string;
  changes: VisualChange[];
  selectedUserId?: string | null;
  selectedChangeId?: string | null;
  onChangeClick?: (changeId: string) => void;
  className?: string;
}

interface ProcessedChange extends VisualChange {
  color: string;
  isVisible: boolean;
  isSelected: boolean;
}

interface ChangeSegment {
  type: 'text' | 'change';
  content: string;
  change?: ProcessedChange;
  startIndex: number;
  endIndex: number;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getUserColor = (userId: string): string => {
  const USER_COLORS = [
    '#3B82F6', // Blue
    '#10B981', // Green  
    '#F59E0B', // Yellow
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#F97316', // Orange
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#EC4899', // Pink
    '#6B7280', // Gray
  ];
  
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return USER_COLORS[hash % USER_COLORS.length];
};

const getChangeStyles = (change: ProcessedChange) => {
  const baseStyles = {
    position: 'relative' as const,
    cursor: 'pointer',
    borderRadius: '2px',
    padding: '1px 2px',
    margin: '0 1px',
    transition: 'all 0.2s ease',
  };

  const opacity = change.isSelected ? 1 : 0.7;
  const borderWidth = change.isSelected ? '2px' : '1px';

  switch (change.type) {
    // Basic operations
    case 'insert':
      return {
        ...baseStyles,
        backgroundColor: `${change.color}20`,
        borderLeft: `${borderWidth} solid ${change.color}`,
        color: change.color,
        fontWeight: change.isSelected ? 'bold' : 'normal',
      };
    
    case 'delete':
      return {
        ...baseStyles,
        backgroundColor: `${change.color}15`,
        textDecoration: 'line-through',
        color: `${change.color}CC`,
        border: `${borderWidth} solid ${change.color}`,
        opacity,
      };
    
    case 'format':
      return {
        ...baseStyles,
        backgroundColor: `${change.color}10`,
        border: `${borderWidth} dashed ${change.color}`,
        color: change.color,
        opacity,
      };

    case 'move':
      return {
        ...baseStyles,
        backgroundColor: `${change.color}15`,
        border: `${borderWidth} dotted ${change.color}`,
        color: change.color,
        opacity,
      };

    // Extended operations
    case 'replace':
      return {
        ...baseStyles,
        backgroundColor: '#F97316' + '20', // Orange
        borderTop: `${borderWidth} solid #F97316`,
        borderBottom: `${borderWidth} solid #F97316`,
        color: '#F97316',
        opacity,
      };

    case 'style':
      return {
        ...baseStyles,
        backgroundColor: '#06B6D4' + '15', // Cyan
        borderBottom: `${borderWidth} solid #06B6D4`,
        color: '#06B6D4',
        opacity,
      };

    case 'structure':
      return {
        ...baseStyles,
        backgroundColor: `${change.color}10`,
        outline: `${borderWidth} solid ${change.color}`,
        outlineOffset: '1px',
        color: change.color,
        opacity,
      };

    case 'annotation':
      return {
        ...baseStyles,
        backgroundColor: '#FEF3C7', // Light yellow
        border: `${borderWidth} solid #F59E0B`,
        color: '#92400E',
        opacity,
      };

    case 'metadata':
      return {
        ...baseStyles,
        backgroundColor: `${change.color}08`,
        border: `${borderWidth} double ${change.color}`,
        color: change.color,
        opacity,
      };

    case 'table':
      return {
        ...baseStyles,
        backgroundColor: '#E0E7FF', // Light indigo
        border: `${borderWidth} solid #6366F1`,
        color: '#4338CA',
        opacity,
      };

    case 'image':
      return {
        ...baseStyles,
        backgroundColor: '#FCE7F3', // Light pink
        border: `${borderWidth} solid #EC4899`,
        color: '#BE185D',
        opacity,
      };

    case 'link':
      return {
        ...baseStyles,
        backgroundColor: '#DBEAFE', // Light blue
        borderBottom: `${borderWidth} solid #3B82F6`,
        color: '#1D4ED8',
        textDecoration: 'underline',
        opacity,
      };

    // Legal document specific
    case 'citation':
      return {
        ...baseStyles,
        backgroundColor: '#FEF3C7', // Light yellow
        border: `${borderWidth} solid #D97706`,
        color: '#92400E',
        fontWeight: 'semibold',
        opacity,
      };

    case 'clause':
      return {
        ...baseStyles,
        backgroundColor: '#F3E8FF', // Light purple
        borderLeft: `3px solid #8B5CF6`,
        paddingLeft: '4px',
        color: '#6B21A8',
        opacity,
      };

    case 'signature':
      return {
        ...baseStyles,
        backgroundColor: '#ECFDF5', // Light green
        border: `${borderWidth} solid #10B981`,
        color: '#047857',
        fontStyle: 'italic',
        opacity,
      };

    case 'header_footer':
      return {
        ...baseStyles,
        backgroundColor: `${change.color}08`,
        borderTop: `${borderWidth} solid ${change.color}`,
        borderBottom: `${borderWidth} solid ${change.color}`,
        color: change.color,
        opacity,
      };

    case 'page_break':
      return {
        ...baseStyles,
        backgroundColor: '#F3F4F6',
        border: `${borderWidth} dashed #6B7280`,
        color: '#374151',
        opacity,
      };

    case 'line_number':
      return {
        ...baseStyles,
        backgroundColor: '#FEF3C7',
        borderLeft: `${borderWidth} solid #F59E0B`,
        color: '#92400E',
        fontFamily: 'monospace',
        opacity,
      };

    // Collaboration types
    case 'suggestion':
      return {
        ...baseStyles,
        backgroundColor: '#FEF3C7', // Light yellow
        border: `${borderWidth} dashed #F59E0B`,
        color: '#92400E',
        opacity: 0.8,
      };

    case 'acceptance':
      return {
        ...baseStyles,
        backgroundColor: '#DCFCE7', // Light green
        border: `${borderWidth} solid #16A34A`,
        color: '#15803D',
        opacity,
      };

    case 'rejection':
      return {
        ...baseStyles,
        backgroundColor: '#FEE2E2', // Light red
        border: `${borderWidth} solid #DC2626`,
        color: '#B91C1C',
        textDecoration: 'line-through',
        opacity,
      };

    case 'comment':
      return {
        ...baseStyles,
        backgroundColor: '#F1F5F9', // Light gray
        borderRight: `3px solid #64748B`,
        color: '#475569',
        opacity,
      };
    
    default:
      return {
        ...baseStyles,
        backgroundColor: `${change.color}15`,
        border: `${borderWidth} solid ${change.color}`,
        opacity,
      };
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DocumentChangeOverlay: React.FC<DocumentChangeOverlayProps> = ({
  documentContent,
  changes,
  selectedUserId,
  selectedChangeId,
  onChangeClick,
  className = ''
}) => {
  // Process changes for rendering
  const processedChanges = useMemo(() => {
    return changes.map(change => ({
      ...change,
      color: getUserColor(change.userId),
      isVisible: !selectedUserId || change.userId === selectedUserId,
      isSelected: change.id === selectedChangeId,
    }));
  }, [changes, selectedUserId, selectedChangeId]);

  // Create segments for rendering
  const segments = useMemo(() => {
    if (!documentContent) return [];

    // Sort changes by position
    const sortedChanges = [...processedChanges]
      .filter(change => change.isVisible)
      .sort((a, b) => a.position.start - b.position.start);

    const segments: ChangeSegment[] = [];
    let currentIndex = 0;

    for (const change of sortedChanges) {
      // Add text before this change
      if (currentIndex < change.position.start) {
        const textContent = documentContent.slice(currentIndex, change.position.start);
        if (textContent) {
          segments.push({
            type: 'text',
            content: textContent,
            startIndex: currentIndex,
            endIndex: change.position.start,
          });
        }
      }

      // Add the change segment
      let changeContent = '';
      
      if (change.type === 'insert') {
        changeContent = change.content || change.newContent || '[inserted text]';
      } else if (change.type === 'delete') {
        changeContent = change.originalContent || '[deleted text]';
      } else {
        changeContent = documentContent.slice(change.position.start, change.position.end) || '[formatted text]';
      }

      segments.push({
        type: 'change',
        content: changeContent,
        change,
        startIndex: change.position.start,
        endIndex: change.position.end,
      });

      currentIndex = Math.max(currentIndex, change.position.end);
    }

    // Add remaining text
    if (currentIndex < documentContent.length) {
      const remainingText = documentContent.slice(currentIndex);
      if (remainingText) {
        segments.push({
          type: 'text',
          content: remainingText,
          startIndex: currentIndex,
          endIndex: documentContent.length,
        });
      }
    }

    return segments;
  }, [documentContent, processedChanges]);

  // Render change tooltip
  const renderChangeTooltip = (change: ProcessedChange) => {
    const formatTimestamp = (timestamp: Date) => {
      return timestamp.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getChangeTypeLabel = (type: string) => {
      switch (type) {
        case 'insert': return 'Added';
        case 'delete': return 'Deleted';
        case 'format': return 'Formatted';
        case 'move': return 'Moved';
        default: return 'Changed';
      }
    };

    return (
      <div className="absolute z-50 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg pointer-events-none whitespace-nowrap -top-10 left-1/2 transform -translate-x-1/2">
        <div className="font-medium">{change.userName}</div>
        <div className="text-gray-300">
          {getChangeTypeLabel(change.type)} • {formatTimestamp(change.timestamp)}
        </div>
        {/* Tooltip arrow */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
    );
  };

  // Handle change click
  const handleChangeClick = (change: ProcessedChange) => {
    onChangeClick?.(change.id);
  };

  if (!documentContent || segments.length === 0) {
    return (
      <div className={`text-gray-500 dark:text-gray-400 ${className}`}>
        {documentContent || 'No content to display'}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
        {segments.map((segment, index) => {
          if (segment.type === 'text') {
            return (
              <span key={`text-${index}`} className="text-gray-900 dark:text-gray-100">
                {segment.content}
              </span>
            );
          } else if (segment.change) {
            return (
              <span
                key={`change-${segment.change.id}`}
                className="relative group inline-block"
                style={getChangeStyles(segment.change)}
                onClick={() => handleChangeClick(segment.change!)}
                title={`${segment.change.userName} ${segment.change.type} - ${segment.change.timestamp.toLocaleString()}`}
              >
                {segment.content}
                
                {/* Hover tooltip */}
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {renderChangeTooltip(segment.change)}
                </div>
              </span>
            );
          }
          return null;
        })}
      </div>

      {/* Legend */}
      {processedChanges.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            Change Types:
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            {/* Dynamic legend based on actual change types present */}
            {processedChanges.some(c => c.isVisible && c.type === 'insert') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 bg-opacity-20 border-l-2 border-blue-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Insertions ({processedChanges.filter(c => c.isVisible && c.type === 'insert').length})
                </span>
              </div>
            )}
            {processedChanges.some(c => c.isVisible && c.type === 'delete') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 bg-opacity-15 border border-red-500 rounded-sm relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-px bg-red-500"></div>
                  </div>
                </div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Deletions ({processedChanges.filter(c => c.isVisible && c.type === 'delete').length})
                </span>
              </div>
            )}
            {processedChanges.some(c => c.isVisible && c.type === 'format') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 bg-opacity-10 border border-dashed border-purple-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Formatting ({processedChanges.filter(c => c.isVisible && c.type === 'format').length})
                </span>
              </div>
            )}
            {processedChanges.some(c => c.isVisible && c.type === 'move') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 bg-opacity-10 border border-green-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Moves ({processedChanges.filter(c => c.isVisible && c.type === 'move').length})
                </span>
              </div>
            )}
            
            {/* INTELLIGENT LEGAL CHANGE TYPES */}
            {processedChanges.some(c => c.isVisible && c.type === 'citation') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-amber-500 bg-opacity-20 border border-amber-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Citations ({processedChanges.filter(c => c.isVisible && c.type === 'citation').length})
                </span>
              </div>
            )}
            {processedChanges.some(c => c.isVisible && c.type === 'clause') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-indigo-500 bg-opacity-20 border border-indigo-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Legal Clauses ({processedChanges.filter(c => c.isVisible && c.type === 'clause').length})
                </span>
              </div>
            )}
            {processedChanges.some(c => c.isVisible && c.type === 'replace') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 bg-opacity-20 border border-orange-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Replacements ({processedChanges.filter(c => c.isVisible && c.type === 'replace').length})
                </span>
              </div>
            )}
            {processedChanges.some(c => c.isVisible && c.type === 'signature') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-emerald-500 bg-opacity-20 border border-emerald-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Signatures ({processedChanges.filter(c => c.isVisible && c.type === 'signature').length})
                </span>
              </div>
            )}
            {processedChanges.some(c => c.isVisible && c.type === 'style') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-pink-500 bg-opacity-20 border border-pink-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Style Changes ({processedChanges.filter(c => c.isVisible && c.type === 'style').length})
                </span>
              </div>
            )}
            {processedChanges.some(c => c.isVisible && c.type === 'table') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-teal-500 bg-opacity-20 border border-teal-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Tables ({processedChanges.filter(c => c.isVisible && c.type === 'table').length})
                </span>
              </div>
            )}
            {processedChanges.some(c => c.isVisible && c.type === 'link') && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-cyan-500 bg-opacity-20 border border-cyan-500 rounded-sm"></div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Links ({processedChanges.filter(c => c.isVisible && c.type === 'link').length})
                </span>
              </div>
            )}
            
            {/* Show inactive types in gray when no changes of that type exist */}
            {!processedChanges.some(c => c.isVisible && c.type === 'insert') && (
              <div className="flex items-center space-x-2 opacity-40">
                <div className="w-3 h-3 bg-gray-400 bg-opacity-20 border-l-2 border-gray-400 rounded-sm"></div>
                <span className="text-gray-400 dark:text-gray-500">Insertions (0)</span>
              </div>
            )}
            {!processedChanges.some(c => c.isVisible && c.type === 'delete') && (
              <div className="flex items-center space-x-2 opacity-40">
                <div className="w-3 h-3 bg-gray-400 bg-opacity-15 border border-gray-400 rounded-sm relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-px bg-gray-400"></div>
                  </div>
                </div>
                <span className="text-gray-400 dark:text-gray-500">Deletions (0)</span>
              </div>
            )}
            {!processedChanges.some(c => c.isVisible && c.type === 'format') && (
              <div className="flex items-center space-x-2 opacity-40">
                <div className="w-3 h-3 bg-gray-400 bg-opacity-10 border border-dashed border-gray-400 rounded-sm"></div>
                <span className="text-gray-400 dark:text-gray-500">Formatting (0)</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentChangeOverlay;
